// En-têtes de colonnes pivot → groupes de colonnes (un groupe par mesure).
import type { ColumnMemberInfo } from "./types";

export function splitHeader(col: string): [string, string] {
  const sep = col.indexOf(" - ");
  if (sep < 0) return [col, col];
  return [col.slice(0, sep), col.slice(sep + 3)];
}

export interface ColChild {
  label: string;
  origIdx: number;
}

export interface ColGroup {
  label: string;
  children: ColChild[];
  /**
   * Colonne « All » de ce groupe : total horizontal calculé par SSAS
   * (connu seulement si le backend renvoie `columnMembers`).
   */
  totalIdx?: number;
}

/**
 * Groupes de colonnes. Avec `columnMembers` (backend récent), le groupe est la
 * mesure et l'enfant le membre en colonnes — sans découper le libellé, ce qui
 * casserait sur des mesures comme « Pttc - Vente » — et la colonne « All » est
 * isolée comme total. Sinon, repli sur le découpage « Parent - Enfant ».
 */
export function buildColGroups(
  dataCols: string[],
  columnMembers?: ColumnMemberInfo[][],
): ColGroup[] {
  const groups: ColGroup[] = [];
  const groupFor = (label: string): ColGroup => {
    const last = groups[groups.length - 1];
    if (last && last.label === label) return last;
    const g: ColGroup = { label, children: [] };
    groups.push(g);
    return g;
  };

  if (columnMembers && columnMembers.length === dataCols.length) {
    dataCols.forEach((col, i) => {
      const members = columnMembers[i] ?? [];
      const measure = members.find((m) => m.isMeasure);
      const dims = members.filter((m) => !m.isMeasure);
      const group = groupFor(measure?.caption ?? col);
      if (dims.length > 0 && dims.every((m) => m.levelDepth === 0)) {
        group.totalIdx = i;
      } else {
        const label = dims.length ? dims.map((m) => m.caption).join(" - ") : (measure?.caption ?? col);
        group.children.push({ label, origIdx: i });
      }
    });
    return groups;
  }

  dataCols.forEach((col, i) => {
    const [parent, child] = splitHeader(col);
    groupFor(parent).children.push({ label: child, origIdx: i });
  });
  return groups;
}
